<template>
  <div class="flex h-screen w-full overflow-hidden bg-slate-100">
    <div class="w-full flex-1 overflow-hidden">
      <Topbar />

      <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-4 md:p-5 lg:p-6">
        <Toast />

        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h1 class="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Аналитика</h1>
            <p class="mt-1 text-sm text-slate-500 md:text-base">
              Продажи, новые клиенты, списания и проданные товары за выбранный период
            </p>
          </div>
          <Button
            label="Обновить"
            icon="pi pi-refresh"
            severity="secondary"
            outlined
            class="!border-slate-200 !shadow-sm"
            :loading="loading"
            @click="fetchAnalytics"
          />
        </div>

        <section class="mb-4 shrink-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div class="grid gap-3 lg:grid-cols-[200px_minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end">
            <div>
              <label class="mb-1 block text-sm font-medium text-slate-700">Период</label>
              <Select
                v-model="selectedPreset"
                :options="presetOptions"
                optionLabel="label"
                optionValue="value"
                class="w-full"
              />
            </div>

            <div>
              <label class="mb-1 block text-sm font-medium text-slate-700">Дата с</label>
              <InputText v-model="filters.from" type="date" class="w-full" @update:modelValue="selectedPreset = 'custom'" />
            </div>

            <div>
              <label class="mb-1 block text-sm font-medium text-slate-700">Дата по</label>
              <InputText v-model="filters.to" type="date" class="w-full" @update:modelValue="selectedPreset = 'custom'" />
            </div>

            <Button
              label="Применить"
              icon="pi pi-filter"
              severity="success"
              class="lg:min-w-36"
              :loading="loading"
              @click="fetchAnalytics"
            />
          </div>
        </section>

        <section class="mb-4 grid shrink-0 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          <article
            v-for="card in summaryCards"
            :key="card.key"
            class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div class="mb-2 flex items-center justify-between">
              <i :class="[card.icon, 'text-lg', card.iconColor]"></i>
              <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">{{ card.meta }}</span>
            </div>
            <p class="text-[28px] font-semibold leading-none text-slate-900">{{ card.value }}</p>
            <p class="mt-2 text-sm text-slate-500">{{ card.label }}</p>
          </article>
        </section>

        <div v-if="loading && !analytics" class="flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white text-sm text-slate-500 shadow-sm">
          Загрузка аналитики...
        </div>

        <div v-else class="min-h-0 flex flex-1 flex-col pr-1">
          <div class="min-h-0 flex flex-1 flex-col pb-1">
            <section class="min-h-0 flex flex-1 flex-col">
              <article class="flex min-h-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div class="mb-4 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div>
                    <h2 class="text-lg font-semibold text-slate-900 md:text-xl">{{ activeChartMeta.title }}</h2>
                    <p class="mt-1 text-sm text-slate-500">{{ activeChartMeta.description }}</p>
                  </div>

                  <div class="flex flex-wrap items-center gap-2">
                    <Button
                      v-for="tab in chartTabs"
                      :key="tab.key"
                      :label="tab.label"
                      size="small"
                      :severity="activeChartTab === tab.key ? 'success' : 'secondary'"
                      :outlined="activeChartTab !== tab.key"
                      class="!rounded-xl"
                      @click="activeChartTab = tab.key"
                    />
                  </div>
                </div>

                <div v-if="activeChartTab === 'sales'" class="min-h-0 flex-1">
                  <VueApexCharts
                    class="analytics-chart"
                    type="line"
                    height="100%"
                    :options="salesChartOptions"
                    :series="salesChartSeries"
                  />
                </div>

                <div v-else-if="activeChartTab === 'activity'" class="min-h-0 flex-1">
                  <VueApexCharts
                    class="analytics-chart"
                    type="line"
                    height="100%"
                    :options="activityChartOptions"
                    :series="activityChartSeries"
                  />
                </div>

                <div
                  v-else-if="activeChartTab === 'all-sales' && !salesRows.length"
                  class="flex h-full min-h-0 flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500"
                >
                  Продаж за выбранный период нет
                </div>

                <div v-else-if="activeChartTab === 'all-sales'" class="min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                  <div class="h-full overflow-auto">
                    <table class="min-w-full border-separate border-spacing-0">
                      <thead class="sticky top-0 z-10">
                        <tr class="bg-white">
                          <th class="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Продажа</th>
                          <th class="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Дата</th>
                          <th class="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Клиент</th>
                          <th class="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Телефон</th>
                          <th class="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Счёт</th>
                          <th class="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Продавец</th>
                          <th class="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Позиций</th>
                          <th class="border-b border-slate-200 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Сумма</th>
                          <th class="border-b border-slate-200 px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Действие</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr
                          v-for="sale in salesRows"
                          :key="sale.id"
                          class="bg-slate-50 transition-colors hover:bg-white"
                        >
                          <td class="border-b border-slate-200/80 px-4 py-3 align-top">
                            <p class="text-sm font-semibold text-slate-900">{{ sale.sale_id || `#${sale.id}` }}</p>
                            <p class="mt-1 text-xs text-slate-500">ID: {{ sale.id }}</p>
                          </td>
                          <td class="border-b border-slate-200/80 px-4 py-3 align-top text-sm text-slate-600">{{ formatDateTime(sale.date) }}</td>
                          <td class="border-b border-slate-200/80 px-4 py-3 align-top text-sm font-medium text-slate-900">{{ sale.client || 'Без клиента' }}</td>
                          <td class="border-b border-slate-200/80 px-4 py-3 align-top text-sm text-slate-600">{{ formatPhone(sale.client_phone) || '—' }}</td>
                          <td class="border-b border-slate-200/80 px-4 py-3 align-top text-sm text-slate-600">{{ getSaleAccountLabel(sale) }}</td>
                          <td class="border-b border-slate-200/80 px-4 py-3 align-top text-sm text-slate-600">{{ sale.employee || '—' }}</td>
                          <td class="border-b border-slate-200/80 px-4 py-3 align-top text-sm font-medium text-slate-700">{{ formatNumber(sale.items_quantity || 0) }}</td>
                          <td class="border-b border-slate-200/80 px-4 py-3 align-top">
                            <p class="text-sm font-semibold text-emerald-700">{{ formatCurrency(sale.price_after_discount) }}</p>
                            <p class="mt-1 text-xs text-slate-500">До скидки: {{ formatCurrency(sale.total_amount) }}</p>
                          </td>
                          <td class="border-b border-slate-200/80 px-4 py-3 align-top text-right">
                            <Button
                              label="Подробнее"
                              icon="pi pi-eye"
                              size="small"
                              severity="secondary"
                              outlined
                              @click="openSaleDetails(sale)"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div
                  v-else-if="activeChartTab === 'top-sold' && !topSoldProducts.length"
                  class="flex h-full min-h-0 flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500"
                >
                  Нет продаж товаров за выбранный период
                </div>

                <div v-else-if="activeChartTab === 'top-sold'" class="min-h-0 flex-1 overflow-hidden">
                  <div class="flex h-full min-h-0 flex-col rounded-2xl border border-slate-200 bg-slate-50">
                    <div class="grid shrink-0 grid-cols-[minmax(0,2.2fr)_minmax(120px,0.9fr)_minmax(140px,1fr)] gap-3 border-b border-slate-200 bg-white px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      <div>Товар</div>
                      <div>Продано</div>
                      <div>Выручка</div>
                    </div>

                    <div class="min-h-0 flex-1 overflow-y-auto">
                      <div
                        v-for="item in topSoldProducts"
                        :key="`top-sold-${item.product_id}`"
                        class="grid grid-cols-[minmax(0,2.2fr)_minmax(120px,0.9fr)_minmax(140px,1fr)] gap-3 border-b border-slate-200/80 px-4 py-3 last:border-b-0"
                      >
                        <div class="min-w-0">
                          <p class="truncate text-sm font-semibold text-slate-900">{{ getProductDisplayName(item) }}</p>
                          <p class="mt-1 truncate text-xs text-slate-500">{{ getProductMetaLabel(item) }}</p>
                        </div>

                        <div
                          class="rounded-xl border border-emerald-200/70 px-3 py-2.5 text-slate-900"
                          :style="getHeatCellStyle(item.quantity, topSoldQuantityMax, 'emerald')"
                        >
                          <p class="text-[10px] uppercase tracking-[0.16em] text-slate-500">Количество</p>
                          <p class="mt-1 text-base font-semibold">{{ formatNumber(item.quantity) }} шт</p>
                        </div>

                        <div
                          class="rounded-xl border border-teal-200/70 px-3 py-2.5 text-slate-900"
                          :style="getHeatCellStyle(item.revenue, topSoldRevenueMax, 'teal')"
                        >
                          <p class="text-[10px] uppercase tracking-[0.16em] text-slate-500">Выручка</p>
                          <p class="mt-1 text-base font-semibold">{{ formatCurrency(item.revenue) }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-else-if="activeChartTab === 'top-writeoff' && !topWriteOffProducts.length"
                  class="flex h-full min-h-0 flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500"
                >
                  Списаний за выбранный период нет
                </div>

                <div v-else-if="activeChartTab === 'top-writeoff'" class="min-h-0 flex-1 overflow-hidden">
                  <div class="flex h-full min-h-0 flex-col rounded-2xl border border-slate-200 bg-slate-50">
                    <div class="grid shrink-0 grid-cols-[minmax(0,2.2fr)_minmax(120px,0.9fr)_minmax(140px,1fr)] gap-3 border-b border-slate-200 bg-white px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      <div>Товар</div>
                      <div>Списано</div>
                      <div>Операции</div>
                    </div>

                    <div class="min-h-0 flex-1 overflow-y-auto">
                      <div
                        v-for="item in topWriteOffProducts"
                        :key="`top-writeoff-${item.product_id}`"
                        class="grid grid-cols-[minmax(0,2.2fr)_minmax(120px,0.9fr)_minmax(140px,1fr)] gap-3 border-b border-slate-200/80 px-4 py-3 last:border-b-0"
                      >
                        <div class="min-w-0">
                          <p class="truncate text-sm font-semibold text-slate-900">{{ getProductDisplayName(item) }}</p>
                          <p class="mt-1 truncate text-xs text-slate-500">{{ getProductMetaLabel(item) }}</p>
                        </div>

                        <div
                          class="rounded-xl border border-rose-200/70 px-3 py-2.5 text-slate-900"
                          :style="getHeatCellStyle(item.quantity, topWriteOffQuantityMax, 'rose')"
                        >
                          <p class="text-[10px] uppercase tracking-[0.16em] text-slate-500">Количество</p>
                          <p class="mt-1 text-base font-semibold">{{ formatNumber(item.quantity) }} шт</p>
                        </div>

                        <div
                          class="rounded-xl border border-amber-200/70 px-3 py-2.5 text-slate-900"
                          :style="getHeatCellStyle(item.write_off_count, topWriteOffCountMax, 'amber')"
                        >
                          <p class="text-[10px] uppercase tracking-[0.16em] text-slate-500">Списаний</p>
                          <p class="mt-1 text-base font-semibold">{{ formatNumber(item.write_off_count) }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="activeChartTab === 'clients' && !recentClients.length"
                  class="flex h-full min-h-0 flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500"
                >
                  Новых клиентов за выбранный период нет
                </div>

                <div v-else-if="activeChartTab === 'clients'" class="min-h-0 flex-1 overflow-hidden">
                  <div class="flex h-full min-h-0 flex-col rounded-2xl border border-slate-200 bg-slate-50">
                    <div class="grid shrink-0 grid-cols-[minmax(0,1.6fr)_minmax(0,1.1fr)_minmax(150px,1fr)_minmax(120px,0.8fr)] gap-3 border-b border-slate-200 bg-white px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      <div>Клиент</div>
                      <div>Телефон</div>
                      <div>Создан</div>
                      <div>Бонусы</div>
                    </div>

                    <div class="min-h-0 flex-1 overflow-y-auto">
                      <div
                        v-for="client in recentClients"
                        :key="client.id"
                        class="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1.1fr)_minmax(150px,1fr)_minmax(120px,0.8fr)] gap-3 border-b border-slate-200/80 px-4 py-3 last:border-b-0"
                      >
                        <div class="min-w-0">
                          <p class="truncate text-sm font-semibold text-slate-900">{{ client.name || `Клиент #${client.id}` }}</p>
                          <p class="mt-1 text-xs text-slate-500">ID: {{ client.id }}</p>
                        </div>

                        <div class="truncate text-sm text-slate-600">
                          {{ formatPhone(client.phone) || 'Телефон не указан' }}
                        </div>

                        <div class="text-sm text-slate-600">
                          {{ formatDateTime(client.created_at) }}
                        </div>

                        <div
                          class="rounded-xl border border-emerald-200/70 px-3 py-2.5 text-slate-900"
                          :style="getHeatCellStyle(client.bonuses, recentClientsBonusesMax, 'emerald')"
                        >
                          <p class="text-[10px] uppercase tracking-[0.16em] text-slate-500">Бонусы</p>
                          <p class="mt-1 text-base font-semibold">{{ formatNumber(client.bonuses) }} ₸</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-else-if="activeChartTab === 'reasons' && !writeOffReasons.length"
                  class="flex h-full min-h-0 flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500"
                >
                  Причин списаний за выбранный период нет
                </div>

                <div v-else-if="activeChartTab === 'reasons'" class="min-h-0 flex-1 overflow-hidden">
                  <div class="flex h-full min-h-0 flex-col rounded-2xl border border-slate-200 bg-slate-50">
                    <div class="grid shrink-0 grid-cols-[minmax(0,2fr)_minmax(140px,0.9fr)_minmax(140px,0.9fr)] gap-3 border-b border-slate-200 bg-white px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                      <div>Причина</div>
                      <div>Списано</div>
                      <div>Операции</div>
                    </div>

                    <div class="min-h-0 flex-1 overflow-y-auto">
                      <div
                        v-for="reason in writeOffReasons"
                        :key="reason.reason"
                        class="grid grid-cols-[minmax(0,2fr)_minmax(140px,0.9fr)_minmax(140px,0.9fr)] gap-3 border-b border-slate-200/80 px-4 py-3 last:border-b-0"
                      >
                        <div class="min-w-0">
                          <p class="truncate text-sm font-semibold text-slate-900">{{ reason.reason }}</p>
                        </div>

                        <div
                          class="rounded-xl border border-rose-200/70 px-3 py-2.5 text-slate-900"
                          :style="getHeatCellStyle(reason.quantity, writeOffReasonQuantityMax, 'rose')"
                        >
                          <p class="text-[10px] uppercase tracking-[0.16em] text-slate-500">Количество</p>
                          <p class="mt-1 text-base font-semibold">{{ formatNumber(reason.quantity) }} шт</p>
                        </div>

                        <div
                          class="rounded-xl border border-amber-200/70 px-3 py-2.5 text-slate-900"
                          :style="getHeatCellStyle(reason.entries_count, writeOffReasonEntriesMax, 'amber')"
                        >
                          <p class="text-[10px] uppercase tracking-[0.16em] text-slate-500">Операций</p>
                          <p class="mt-1 text-base font-semibold">{{ formatNumber(reason.entries_count) }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </section>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Dialog
    v-model:visible="showSaleDetailsDialog"
    modal
    header="Детали продажи"
    :style="{ width: '1040px', maxWidth: '96vw' }"
  >
    <div v-if="saleDetailsLoading" class="py-6 text-base text-gray-500">Загрузка деталей...</div>
    <div v-else-if="!selectedSaleDetails" class="py-6 text-base text-gray-500">Данные по продаже не найдены</div>

    <div v-else class="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
      <section class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <h3 class="mb-3 text-lg font-semibold text-slate-900">Продажа</h3>
        <div class="space-y-3 text-sm">
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Номер</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.sale_id }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Дата</p>
            <p class="mt-1 font-semibold text-slate-900">{{ formatDateTime(selectedSaleDetails.date) }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Клиент</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.client?.name || 'Без клиента' }}</p>
            <p class="mt-1 text-slate-500">{{ formatPhone(selectedSaleDetails.client?.phone) || 'Телефон не указан' }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Продавец</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.employee?.name || '—' }}</p>
          </div>
          <div class="rounded-xl border border-slate-200 bg-white px-3 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">Счёт</p>
            <p class="mt-1 font-semibold text-slate-900">{{ selectedSaleDetails.bank }} · {{ selectedSaleDetails.account }}</p>
          </div>
        </div>
      </section>

      <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div class="mx-auto w-full max-w-3xl rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-4 md:p-5">
          <div class="border-b border-dashed border-slate-300 pb-4 text-center">
            <p class="text-xs font-medium uppercase tracking-[0.22em] text-slate-500">Чек продажи</p>
            <p class="mt-2 text-2xl font-semibold text-slate-900">{{ selectedSaleDetails.sale_id }}</p>
            <p class="mt-1 text-sm text-slate-500">{{ formatDateTime(selectedSaleDetails.date) }}</p>
          </div>

          <div class="mt-4 space-y-2 border-b border-dashed border-slate-300 pb-4 text-sm text-slate-600">
            <div class="flex items-start justify-between gap-3">
              <span>Клиент</span>
              <div class="text-right">
                <p class="font-medium text-slate-900">{{ selectedSaleDetails.client?.name || 'Без клиента' }}</p>
                <p class="text-xs text-slate-500">{{ formatPhone(selectedSaleDetails.client?.phone) || 'Телефон не указан' }}</p>
              </div>
            </div>
            <div class="flex items-start justify-between gap-3">
              <span>Счёт</span>
              <span class="text-right font-medium text-slate-900">{{ selectedSaleDetails.bank }} · {{ selectedSaleDetails.account }}</span>
            </div>
            <div class="flex items-start justify-between gap-3">
              <span>Продавец</span>
              <span class="text-right font-medium text-slate-900">{{ selectedSaleDetails.employee?.name || '—' }}</span>
            </div>
          </div>

          <div class="mt-4 space-y-3">
            <article
              v-for="item in saleReceiptItems"
              :key="item.id"
              class="rounded-2xl border border-slate-200 bg-slate-50 p-3"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="truncate text-base font-semibold text-slate-900">{{ item.label }}</p>
                  <p class="mt-1 text-xs text-slate-500">{{ item.meta }}</p>
                </div>
                <div class="shrink-0 text-right">
                  <p class="text-base font-semibold text-slate-900">{{ formatCurrency(item.total_price) }}</p>
                  <p class="mt-1 text-xs text-slate-500">{{ item.quantityLabel }}</p>
                </div>
              </div>

              <div v-if="item.showComposition" class="mt-3">
                <button
                  type="button"
                  class="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                  @click="toggleSaleItemDetails(item.id)"
                >
                  <span class="font-medium">
                    Состав позиции
                    <span class="text-slate-500">({{ item.components.length }})</span>
                  </span>
                  <i
                    class="pi text-xs text-slate-500"
                    :class="isSaleItemExpanded(item.id) ? 'pi-chevron-up' : 'pi-chevron-down'"
                  />
                </button>

                <div
                  v-if="isSaleItemExpanded(item.id)"
                  class="mt-2 space-y-2 rounded-xl border border-slate-200 bg-white p-3"
                >
                  <div
                    v-for="component in item.components"
                    :key="`${item.id}-${component.product_id}-${component.barcode}`"
                    class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5"
                  >
                    <div class="flex items-start justify-between gap-3 text-sm">
                      <div class="min-w-0">
                        <p class="truncate font-medium text-slate-900">{{ component.name }}</p>
                        <p class="truncate text-xs text-slate-500">{{ component.barcode || 'Штрихкод не указан' }}</p>
                        <p class="mt-1 truncate text-xs text-slate-400">
                          {{ component.classification || 'Без класса' }} · {{ component.category || 'Без категории' }}
                        </p>
                      </div>
                      <div class="shrink-0 text-right text-xs text-slate-600">
                        <p>{{ Number(component.quantity || 0) }} × {{ formatCurrency(component.unit_price) }}</p>
                        <p class="mt-1 font-semibold text-slate-900">{{ formatCurrency(component.total_price) }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div class="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <div class="flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Сумма по чеку</span>
              <span class="font-medium text-slate-900">{{ formatCurrency(selectedSaleDetails.total_amount) }}</span>
            </div>
            <div v-if="saleDiscountAmount > 0" class="mt-2 flex items-center justify-between gap-3 text-sm text-slate-700">
              <span>Скидка / бонусы</span>
              <span class="font-medium text-emerald-700">-{{ formatCurrency(saleDiscountAmount) }}</span>
            </div>
            <div class="mt-3 flex items-center justify-between gap-3 border-t border-dashed border-emerald-200 pt-3">
              <span class="text-base font-semibold text-slate-900">К оплате</span>
              <span class="text-2xl font-semibold text-slate-900">{{ formatCurrency(selectedSaleDetails.price_after_discount) }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <Button label="Закрыть" severity="secondary" outlined @click="showSaleDetailsDialog = false" />
    </template>
  </Dialog>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import axios from '../lib/axios'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'
import VueApexCharts from 'vue3-apexcharts'
import Topbar from './Topbar.vue'

const toast = useToast()
const loading = ref(false)
const analytics = ref(null)
const salesRows = ref([])
const saleDetailsLoading = ref(false)
const selectedSaleDetails = ref(null)
const showSaleDetailsDialog = ref(false)
const expandedSaleItemIds = ref([])
const selectedPreset = ref('30')
const activeChartTab = ref('sales')
const filters = ref({
  from: '',
  to: '',
})
let autoFetchTimer = null
const SINGLE_PRODUCT_ORDER_TYPE_ID = 8
const EXTRA_WORK_ORDER_TYPE_ID = 9

const presetOptions = [
  { label: 'Последние 7 дней', value: '7' },
  { label: 'Последние 30 дней', value: '30' },
  { label: 'Последние 90 дней', value: '90' },
  { label: 'Текущий месяц', value: 'month' },
  { label: 'Произвольный период', value: 'custom' },
]

const chartTabs = [
  {
    key: 'sales',
    label: 'Выручка',
    title: 'Динамика выручки',
    description: 'Выручка по дням и количество чеков',
  },
  {
    key: 'activity',
    label: 'Активность',
    title: 'Операционная активность',
    description: 'Новые клиенты, продажи и списания по дням',
  },
  {
    key: 'all-sales',
    label: 'Все продажи',
    title: 'Все продажи',
    description: 'Таблица всех продаж за выбранный период',
  },
  {
    key: 'top-sold',
    label: 'Топ продаж',
    title: 'Топ проданных товаров',
    description: 'Лидеры по количеству продаж за период',
  },
  {
    key: 'top-writeoff',
    label: 'Топ списаний',
    title: 'Топ списаний',
    description: 'Товары, которые списывались чаще всего',
  },
  {
    key: 'clients',
    label: 'Новые клиенты',
    title: 'Новые клиенты',
    description: 'Последние добавленные клиенты в выбранном периоде',
  },
  {
    key: 'reasons',
    label: 'Причины списаний',
    title: 'Причины списаний',
    description: 'Наиболее частые причины в выбранном периоде',
  },
]

const activeChartMeta = computed(() => {
  return chartTabs.find((tab) => tab.key === activeChartTab.value) || chartTabs[0]
})

const toIsoDate = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const applyPreset = (preset) => {
  const today = new Date()
  const end = new Date(today)
  const start = new Date(today)

  if (preset === '7') {
    start.setDate(start.getDate() - 6)
  } else if (preset === '30') {
    start.setDate(start.getDate() - 29)
  } else if (preset === '90') {
    start.setDate(start.getDate() - 89)
  } else if (preset === 'month') {
    start.setDate(1)
  } else {
    return
  }

  filters.value = {
    from: toIsoDate(start),
    to: toIsoDate(end),
  }
}

const hasValidDateRange = () => {
  if (!filters.value.from || !filters.value.to) {
    return false
  }

  return filters.value.from <= filters.value.to
}

const scheduleFetchAnalytics = () => {
  if (!hasValidDateRange()) {
    return
  }

  if (autoFetchTimer) {
    clearTimeout(autoFetchTimer)
  }

  autoFetchTimer = window.setTimeout(() => {
    fetchAnalytics()
  }, 250)
}

watch(selectedPreset, (value) => {
  if (value !== 'custom') {
    applyPreset(value)
  }
})

watch(
  () => [filters.value.from, filters.value.to],
  ([from, to], [previousFrom, previousTo]) => {
    if (from === previousFrom && to === previousTo) {
      return
    }

    scheduleFetchAnalytics()
  }
)

const fetchAnalytics = async () => {
  loading.value = true

  try {
    const params = {
      from: filters.value.from || undefined,
      to: filters.value.to || undefined,
    }

    const [analyticsRes, salesRes] = await Promise.all([
      axios.get('/api/analytics', { params }),
      axios.get('/api/sale', { params }),
    ])

    analytics.value = analyticsRes.data
    salesRows.value = Array.isArray(salesRes.data) ? salesRes.data : []
  } catch (error) {
    const message = error?.response?.data?.message || 'Не удалось загрузить аналитику'
    toast.add({ severity: 'error', summary: 'Ошибка', detail: message, life: 4000 })
  } finally {
    loading.value = false
  }
}

const totals = computed(() => analytics.value?.totals || {})
const trendSeries = computed(() => analytics.value?.series || [])
const topSoldProducts = computed(() => analytics.value?.top_sold_products || [])
const topWriteOffProducts = computed(() => analytics.value?.top_write_off_products || [])
const writeOffReasons = computed(() => analytics.value?.write_off_reasons || [])
const recentClients = computed(() => analytics.value?.recent_clients || [])
const recentClientsBonusesMax = computed(() => getMetricMax(recentClients.value, 'bonuses'))
const writeOffReasonQuantityMax = computed(() => getMetricMax(writeOffReasons.value, 'quantity'))
const writeOffReasonEntriesMax = computed(() => getMetricMax(writeOffReasons.value, 'entries_count'))

const saleReceiptItems = computed(() => {
  const items = selectedSaleDetails.value?.items || []

  return items.map((item, index) => {
    const components = Array.isArray(item.components) ? item.components : []
    const primaryName = item.title
      ? String(item.title).trim()
      : Number(item.order_type_id || 0) === EXTRA_WORK_ORDER_TYPE_ID
        ? 'Доп работа'
        : components.length === 1
          ? components[0]?.name
          : item.product_type || null

    return {
      ...item,
      label: primaryName || `Позиция ${index + 1}`,
      showComposition: Number(item.order_type_id || 0) !== SINGLE_PRODUCT_ORDER_TYPE_ID && components.length > 0,
      meta: [
        item.product_type,
        item.product_level,
        components.length > 1 ? `${components.length} компонентов` : null,
      ].filter(Boolean).join(' · '),
      quantityLabel: `Кол-во: ${Number(item.quantity || 0) || 1}`,
      components,
    }
  })
})

const saleDiscountAmount = computed(() => {
  const total = Number(selectedSaleDetails.value?.total_amount || 0)
  const payable = Number(selectedSaleDetails.value?.price_after_discount || 0)
  return Math.max(0, total - payable)
})

const formatNumber = (value) => Number(value || 0).toLocaleString('ru-RU')
const formatCurrency = (value) => `${formatNumber(value)} ₸`

const formatDate = (value) => {
  if (!value) return ''
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return String(value)

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

const formatDateTime = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  return `${day}.${month}.${year} ${hours}:${minutes}`
}

const formatPhone = (value) => {
  const digits = String(value ?? '').replace(/\D+/g, '')
  if (!digits) return ''

  const normalized = digits.length === 10 ? `7${digits}` : digits
  if (normalized.length !== 11 || !normalized.startsWith('7')) {
    return String(value ?? '')
  }

  return `+${normalized[0]}(${normalized.slice(1, 4)})${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`
}

const getSaleAccountLabel = (sale) => {
  const parts = [sale?.account_name, sale?.account]
    .map((value) => String(value || '').trim())
    .filter(Boolean)

  return parts.length ? parts.join(' · ') : '—'
}

const isSaleItemExpanded = (itemId) => expandedSaleItemIds.value.includes(itemId)

const toggleSaleItemDetails = (itemId) => {
  if (isSaleItemExpanded(itemId)) {
    expandedSaleItemIds.value = expandedSaleItemIds.value.filter((id) => id !== itemId)
    return
  }

  expandedSaleItemIds.value = [...expandedSaleItemIds.value, itemId]
}

const openSaleDetails = async (sale) => {
  if (!sale?.id) return

  showSaleDetailsDialog.value = true
  saleDetailsLoading.value = true
  selectedSaleDetails.value = null
  expandedSaleItemIds.value = []

  try {
    const res = await axios.get(`/api/sale-items/${sale.id}`)
    selectedSaleDetails.value = res.data || null
    expandedSaleItemIds.value = (selectedSaleDetails.value?.items || [])
      .filter((item) => Number(item.order_type_id || 0) !== SINGLE_PRODUCT_ORDER_TYPE_ID && Array.isArray(item.components) && item.components.length > 0)
      .slice(0, 1)
      .map((item) => item.id)
  } catch (error) {
    selectedSaleDetails.value = null
    expandedSaleItemIds.value = []
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error?.response?.data?.message || 'Не удалось загрузить детали продажи',
      life: 4000,
    })
  } finally {
    saleDetailsLoading.value = false
  }
}

const getProductDisplayName = (item) => {
  return item?.name_ru || item?.name || `Товар #${item?.product_id ?? '—'}`
}

const getProductMetaLabel = (item) => {
  const parts = [item?.classification, item?.category]
    .map((value) => String(value || '').trim())
    .filter(Boolean)

  return parts.length ? parts.join(' / ') : 'Без категории'
}

const getMetricMax = (items, key) => {
  return Math.max(0, ...(items || []).map((item) => Number(item?.[key] || 0)))
}

const getHeatRatio = (value, max) => {
  if (!max) return 0

  const normalized = Number(value || 0) / max
  return Math.max(0, Math.min(normalized, 1))
}

const getHeatCellStyle = (value, max, palette = 'emerald') => {
  const ratio = getHeatRatio(value, max)
  const alpha = 0.08 + ratio * 0.34

  const palettes = {
    emerald: {
      bg: `rgba(16, 185, 129, ${alpha})`,
      shadow: `inset 0 0 0 1px rgba(16, 185, 129, ${0.12 + ratio * 0.18})`,
    },
    teal: {
      bg: `rgba(13, 148, 136, ${alpha})`,
      shadow: `inset 0 0 0 1px rgba(13, 148, 136, ${0.12 + ratio * 0.18})`,
    },
    rose: {
      bg: `rgba(244, 63, 94, ${alpha})`,
      shadow: `inset 0 0 0 1px rgba(244, 63, 94, ${0.12 + ratio * 0.18})`,
    },
    amber: {
      bg: `rgba(245, 158, 11, ${alpha})`,
      shadow: `inset 0 0 0 1px rgba(245, 158, 11, ${0.12 + ratio * 0.18})`,
    },
  }

  const selectedPalette = palettes[palette] || palettes.emerald

  return {
    background: selectedPalette.bg,
    boxShadow: selectedPalette.shadow,
  }
}

const formattedPeriod = computed(() => {
  const period = analytics.value?.period
  if (!period?.from || !period?.to) return 'Период не выбран'
  return `${formatDate(period.from)} - ${formatDate(period.to)}`
})

const averageCheck = computed(() => {
  const checks = Number(totals.value.checks_count || 0)
  if (!checks) return 0
  return Math.round(Number(totals.value.sales_amount || 0) / checks)
})

const summaryCards = computed(() => [
  {
    key: 'sales',
    label: 'Выручка',
    value: formatCurrency(totals.value.sales_amount || 0),
    meta: `Средний чек ${formatCurrency(averageCheck.value)}`,
    icon: 'pi pi-wallet',
    iconColor: 'text-emerald-700',
  },
  {
    key: 'checks',
    label: 'Чеков',
    value: formatNumber(totals.value.checks_count || 0),
    meta: `Скидки ${formatCurrency(totals.value.discount_amount || 0)}`,
    icon: 'pi pi-shopping-cart',
    iconColor: 'text-sky-700',
  },
  {
    key: 'clients',
    label: 'Новых клиентов',
    value: formatNumber(totals.value.new_clients_count || 0),
    meta: 'Добавлены за период',
    icon: 'pi pi-user-plus',
    iconColor: 'text-violet-700',
  },
  {
    key: 'sold',
    label: 'Продано, шт',
    value: formatNumber(totals.value.sold_quantity || 0),
    meta: `${formatNumber(totals.value.sold_products_count || 0)} SKU`,
    icon: 'pi pi-box',
    iconColor: 'text-emerald-700',
  },
  {
    key: 'writeoff',
    label: 'Списано, шт',
    value: formatNumber(totals.value.write_off_quantity || 0),
    meta: `${formatNumber(totals.value.write_off_count || 0)} операций`,
    icon: 'pi pi-trash',
    iconColor: 'text-rose-700',
  },
  {
    key: 'writeoff-products',
    label: 'Товаров в списаниях',
    value: formatNumber(totals.value.write_off_products_count || 0),
    meta: 'Уникальные позиции',
    icon: 'pi pi-exclamation-triangle',
    iconColor: 'text-amber-700',
  },
])

const chartBaseOptions = {
  chart: {
    toolbar: { show: false },
    zoom: { enabled: false },
    fontFamily: 'inherit',
  },
  grid: {
    borderColor: '#e2e8f0',
    strokeDashArray: 4,
  },
  dataLabels: {
    enabled: false,
  },
  legend: {
    position: 'top',
    horizontalAlign: 'left',
    fontSize: '12px',
    labels: {
      colors: '#475569',
    },
  },
  stroke: {
    curve: 'smooth',
    width: 3,
  },
  xaxis: {
    categories: trendSeries.value.map((item) => item.label),
    labels: {
      style: {
        colors: '#64748b',
        fontSize: '11px',
      },
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: {
      style: {
        colors: '#64748b',
        fontSize: '11px',
      },
    },
  },
  tooltip: {
    shared: true,
    intersect: false,
  },
}

const salesChartSeries = computed(() => [
  {
    name: 'Выручка',
    type: 'column',
    data: trendSeries.value.map((item) => Number(item.sales_amount || 0)),
  },
  {
    name: 'Чеки',
    type: 'line',
    data: trendSeries.value.map((item) => Number(item.checks_count || 0)),
  },
])

const salesChartOptions = computed(() => ({
  ...chartBaseOptions,
  colors: ['#0f766e', '#2563eb'],
  stroke: {
    curve: 'smooth',
    width: [0, 3],
  },
  plotOptions: {
    bar: {
      columnWidth: '44%',
      borderRadius: 6,
    },
  },
  xaxis: {
    ...chartBaseOptions.xaxis,
    categories: trendSeries.value.map((item) => item.label),
  },
  yaxis: [
    {
      title: {
        text: '₸',
        style: { color: '#64748b' },
      },
      labels: {
        formatter: (value) => formatNumber(value),
        style: {
          colors: '#64748b',
          fontSize: '11px',
        },
      },
    },
    {
      opposite: true,
      title: {
        text: 'Чеки',
        style: { color: '#64748b' },
      },
      labels: {
        formatter: (value) => formatNumber(value),
        style: {
          colors: '#64748b',
          fontSize: '11px',
        },
      },
    },
  ],
  tooltip: {
    shared: true,
    intersect: false,
    y: {
      formatter: (value, { seriesIndex }) => seriesIndex === 0 ? formatCurrency(value) : formatNumber(value),
    },
  },
}))

const activityChartSeries = computed(() => [
  {
    name: 'Новые клиенты',
    data: trendSeries.value.map((item) => Number(item.new_clients_count || 0)),
  },
  {
    name: 'Продано, шт',
    data: trendSeries.value.map((item) => Number(item.sold_quantity || 0)),
  },
  {
    name: 'Списано, шт',
    data: trendSeries.value.map((item) => Number(item.write_off_quantity || 0)),
  },
])

const activityChartOptions = computed(() => ({
  ...chartBaseOptions,
  colors: ['#7c3aed', '#059669', '#e11d48'],
  xaxis: {
    ...chartBaseOptions.xaxis,
    categories: trendSeries.value.map((item) => item.label),
  },
  yaxis: {
    labels: {
      formatter: (value) => formatNumber(value),
      style: {
        colors: '#64748b',
        fontSize: '11px',
      },
    },
  },
  tooltip: {
    shared: true,
    intersect: false,
    y: {
      formatter: (value) => `${formatNumber(value)} шт`,
    },
  },
}))

const topSoldChartSeries = computed(() => [
  {
    name: 'Продано',
    data: topSoldProducts.value.map((item) => Number(item.quantity || 0)),
  },
])

const topSoldQuantityMax = computed(() => getMetricMax(topSoldProducts.value, 'quantity'))
const topSoldRevenueMax = computed(() => getMetricMax(topSoldProducts.value, 'revenue'))

const topSoldChartOptions = computed(() => ({
  chart: {
    toolbar: { show: false },
    fontFamily: 'inherit',
  },
  colors: ['#0f766e'],
  plotOptions: {
    bar: {
      horizontal: true,
      borderRadius: 6,
      barHeight: '52%',
    },
  },
  dataLabels: {
    enabled: false,
  },
  grid: {
    borderColor: '#e2e8f0',
    strokeDashArray: 4,
  },
  xaxis: {
    categories: topSoldProducts.value.map((item) => item.name_ru || item.name || `Товар #${item.product_id}`),
    labels: {
      style: {
        colors: '#64748b',
        fontSize: '11px',
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: '#475569',
        fontSize: '12px',
      },
    },
  },
  tooltip: {
    y: {
      formatter: (value, { dataPointIndex }) => {
        const item = topSoldProducts.value[dataPointIndex]
        return `${formatNumber(value)} шт · ${formatCurrency(item?.revenue || 0)}`
      },
    },
  },
}))

const topWriteOffChartSeries = computed(() => [
  {
    name: 'Списано',
    data: topWriteOffProducts.value.map((item) => Number(item.quantity || 0)),
  },
])

const topWriteOffQuantityMax = computed(() => getMetricMax(topWriteOffProducts.value, 'quantity'))
const topWriteOffCountMax = computed(() => getMetricMax(topWriteOffProducts.value, 'write_off_count'))

const topWriteOffChartOptions = computed(() => ({
  chart: {
    toolbar: { show: false },
    fontFamily: 'inherit',
  },
  colors: ['#e11d48'],
  plotOptions: {
    bar: {
      horizontal: true,
      borderRadius: 6,
      barHeight: '52%',
    },
  },
  dataLabels: {
    enabled: false,
  },
  grid: {
    borderColor: '#e2e8f0',
    strokeDashArray: 4,
  },
  xaxis: {
    categories: topWriteOffProducts.value.map((item) => item.name_ru || item.name || `Товар #${item.product_id}`),
    labels: {
      style: {
        colors: '#64748b',
        fontSize: '11px',
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: '#475569',
        fontSize: '12px',
      },
    },
  },
  tooltip: {
    y: {
      formatter: (value, { dataPointIndex }) => {
        const item = topWriteOffProducts.value[dataPointIndex]
        return `${formatNumber(value)} шт · ${formatNumber(item?.write_off_count || 0)} операций`
      },
    },
  },
}))

onMounted(() => {
  applyPreset(selectedPreset.value)
})

onBeforeUnmount(() => {
  if (autoFetchTimer) {
    clearTimeout(autoFetchTimer)
  }
})
</script>

<style scoped>
:deep(.analytics-chart .apexcharts-tooltip) {
  border-radius: 0.9rem;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
}

:deep(.analytics-chart .apexcharts-legend-series) {
  margin-right: 10px !important;
}
</style>
